import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  Divider, 
  Grid,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Breadcrumbs,
  Link,
  Button,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import mermaid from 'mermaid';
import { Timeline } from 'vis-timeline/standalone';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import UpdateIcon from '@mui/icons-material/Update';
import UpdateDisabledIcon from '@mui/icons-material/UpdateDisabled';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import WarningIcon from '@mui/icons-material/Warning';
import RecoveryIcon from '@mui/icons-material/Healing';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import 'vis-timeline/styles/vis-timeline-graph2d.css';
import { useGetWorkflowExecution, useStartWorkflow, useGetWorkflowLogFile, useWorkflowStepRecover, useTriggerScheduledStepEarly } from '../../../clients/graphql/pages/workflow-queries';import { DataSet } from 'vis-data';
import { JsonViewer } from '../../../components/JsonViewer';
import { LinearAppIcon } from '../../../components/icons/LinearAppIcon';

interface TimelineChartProps {
  items: any;
  groups: any;
  options: any;
}

const TimelineChart: React.FC<TimelineChartProps> = ({ items, groups, options }) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineInstanceRef = useRef<any>(null);

  useEffect(() => {
    if (timelineRef.current) {
      if (timelineInstanceRef.current) {
        timelineInstanceRef.current.destroy();
      }
      
      timelineInstanceRef.current = new Timeline(timelineRef.current, items, groups, options);
    }

    return () => {
      if (timelineInstanceRef.current) {
        timelineInstanceRef.current.destroy();
        timelineInstanceRef.current = null;
      }
    };
  }, [items, groups, options]);

  return <div ref={timelineRef} style={{ height: '100%' }}/>;
};

// Add CustomJsonEditor component after the existing imports and before the Step interface
const CustomJsonEditor: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [lineNumbers, setLineNumbers] = useState<string>("1");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const lines = value.split("\n").length;
      setLineNumbers(Array.from({ length: lines }, (_, i) => i + 1).join("\n"));
    } else {
      setLineNumbers("1");
    }
  }, [value]);

  const handleScroll = () => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "400px",
        border: "1px solid #ccc",
        borderRadius: "4px",
      }}
    >
      <Box
        ref={lineNumbersRef}
        sx={{
          width: "40px",
          padding: "8px",
          backgroundColor: "#f5f5f5",
          borderRight: "1px solid #ccc",
          overflow: "hidden",
          fontFamily: "monospace",
          fontSize: "14px",
          lineHeight: "20px",
          color: "#666",
          userSelect: "none",
        }}
      >
        {lineNumbers}
      </Box>
      <TextField
        inputRef={textareaRef}
        multiline
        fullWidth
        value={value}
        onChange={handleChange}
        onScroll={handleScroll}
        sx={{
          "& .MuiInputBase-root": {
            height: "100%",
            fontFamily: "monospace",
            fontSize: "14px",
            lineHeight: "20px",
            padding: "8px",
          },
          "& .MuiInputBase-input": {
            height: "100% !important",
            overflow: "auto !important",
          },
        }}
        InputProps={{
          disableUnderline: true,
        }}
      />
    </Box>
  );
};

interface Step {
  executionId: string;
  prevExecutionId: string | null;
  originalStepId?: string | null;
  attempt?: number | null;
  workflowId: string;
  stepName: string;
  scheduledFor?: string | null;
  startedAt: string | null;
  endAt: string | null;
  triggeredAt: string | null;
  inputPayload?: any;
  outputResults?: any;
  logFiles?: {
    stdout: string | null;
    stderr: string | null;
  };
  spanType?: string;
  outgoingJobs?: Record<string, string> | string;
  outgoingSentAt?: string | null;
  executionStatus?: {
    isRunning: boolean;
    lastHeartbeatAt: string | null;
  };
}

// Countdown component for delayed steps
const CountdownTimer: React.FC<{ scheduledFor: string }> = ({ scheduledFor }) => {
  const [timeLeft, setTimeLeft] = useState<string>('');

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date().getTime();
      const scheduledTime = new Date(scheduledFor).getTime();
      const difference = scheduledTime - now;

      if (difference <= 0) {
        setTimeLeft('Starting...');
        return;
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setTimeLeft(`${minutes}m ${seconds}s`);
      } else {
        setTimeLeft(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [scheduledFor]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <ScheduleIcon sx={{ fontSize: '0.875rem', color: 'warning.main' }} />
      <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500 }}>
        {timeLeft}
      </Typography>
    </Box>
  );
};

const getStatusColor = (step: Step) => {
  if (step.endAt) {
    // Check if the step has an error in its output
    const hasError = step.outputResults?.error || (typeof step.outputResults === 'object' && step.outputResults?.success === false);
    return hasError ? 'error' : 'success';
  }
  if (step.startedAt) return 'primary';
  if (step.scheduledFor && new Date(step.scheduledFor) > new Date()) return 'warning';
  return 'default';
};

const formatTimeDifference = (start: Date, end: Date): string => {
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 1000) {
    return `${diffMs}ms`;
  }
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  
  if (diffHour > 0) {
    return `${diffHour}h ${diffMin % 60}m`;
  } else if (diffMin > 0) {
    return `${diffMin}m ${diffSec % 60}s`;
  } else {
    return `${diffSec}s`;
  }
};

interface LogViewerProps {
  workflowId: string;
  stepId: string;
}

const LogViewer: React.FC<LogViewerProps> = ({ workflowId, stepId }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [stdoutContent, setStdoutContent] = useState<string>('');
  const [stderrContent, setStderrContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  const { data: stdoutData, loading: stdoutLoading } = useGetWorkflowLogFile({
    variables: {
      input: {
        workflowId,
        stepId,
        filename: "stdout.log"
      }
    },
    skip: !workflowId || !stepId
  });

  const { data: stderrData, loading: stderrLoading } = useGetWorkflowLogFile({
    variables: {
      input: {
        workflowId,
        stepId,
        filename: "stderr.log"
      }
    },
    skip: !workflowId || !stepId
  });

  useEffect(() => {
    if (!stdoutLoading && !stderrLoading) {
      setIsLoading(false);
      setStdoutContent(stdoutData?.workflowLogFile?.content || 'No stdout log available');
      setStderrContent(stderrData?.workflowLogFile?.content || 'No stderr log available');
    }
  }, [stdoutLoading, stderrLoading, stdoutData, stderrData]);

  const renderLogContent = (content: string) => {
    const lines = content.split('\n');
    return (
      <Box sx={{ display: 'flex', height: '100%', overflow: 'auto' }}>
        <Box sx={{ 
          padding: '0.5rem 1rem',
          borderRight: '1px solid #333',
          color: '#666',
          userSelect: 'none',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          lineHeight: 1.5
        }}>
          {lines.map((_, index) => (
            <div key={index}>{index + 1}</div>
          ))}
        </Box>
        <Box sx={{ 
          padding: '0.5rem 1rem',
          flex: 1,
          fontFamily: 'monospace',
          fontSize: '0.875rem',
          lineHeight: 1.5
        }}>
          {lines.map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
        <Tab label="stdout" />
        <Tab label="stderr" />
      </Tabs>
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        bgcolor: '#1e1e1e', 
        color: '#fff',
        minHeight: '60vh'
      }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          renderLogContent(activeTab === 0 ? stdoutContent : stderrContent)
        )}
      </Box>
    </Box>
  );
};

const filterNonWorkflowSteps = (steps: Step[]): Step[] => {
  return steps.filter(step => step.spanType !== 'WORKFLOW');
};

const addMermaidClickHandlers = (
  element: HTMLElement, 
  steps: Step[], 
  showAllExecutions: boolean,
  onStepClick: (step: Step) => void
) => {
  // Normalize IDs that Mermaid augments (e.g., flowchart-step_<id>-15 -> <id>)
  const normalizeExecutionId = (raw: string): string => {
    return raw.replace(/-\d+$/, '');
  };
  
  // Find all nodes in the SVG - try multiple selectors
  let nodes = element.querySelectorAll('g.node');
  if (nodes.length === 0) {
    nodes = element.querySelectorAll('.node');
  }
  if (nodes.length === 0) {
    nodes = element.querySelectorAll('g[id]');
  }
  
  // Early return if no nodes found
  if (!nodes.length) {
    return;
  }
  
  // Create maps for quick step lookup
  const stepMap = new Map<string, Step>();
  const stepNameMap = new Map<string, Step>();
  
  // Pre-process steps for efficient lookup
  steps.forEach(step => {
    stepMap.set(step.executionId, step);
    
    // For regular mode, keep track of the latest step for each name
    if (!stepNameMap.has(step.stepName) || 
        (step.triggeredAt && (!stepNameMap.get(step.stepName)?.triggeredAt || 
         step.triggeredAt > stepNameMap.get(step.stepName)!.triggeredAt!))) {
      stepNameMap.set(step.stepName, step);
    }
  });
  
  // Process nodes in batches to avoid stack overflow
  const batchSize = 50;
  const processNodeBatch = (startIndex: number) => {
    const endIndex = Math.min(startIndex + batchSize, nodes.length);
    
    for (let i = startIndex; i < endIndex; i++) {
      const node = nodes[i];
      const nodeElement = node as HTMLElement;
      const nodeId = nodeElement.getAttribute('id') || '';
      
      // Try different ways to get text content
      const textElement = nodeElement.querySelector('text') || nodeElement.querySelector('span');
      const nodeText = textElement?.textContent?.trim() || '';
      
      let matchingStep: Step | undefined;
      
      if (showAllExecutions) {
        // For show all executions mode, prioritize ID matching
        if (nodeId) {
          const markerIndex = nodeId.indexOf('step_');
          if (markerIndex >= 0) {
            const rawExecutionId = nodeId.substring(markerIndex + 'step_'.length);
            const executionId = normalizeExecutionId(rawExecutionId);
            matchingStep = stepMap.get(executionId);
          }
        }
        
        // Fallback to text matching if ID matching failed
        if (!matchingStep && nodeText) {
          const executionIdMatch = nodeText.match(/\(([^)]+)\)$/);
          if (executionIdMatch) {
            const executionId = normalizeExecutionId(executionIdMatch[1]);
            matchingStep = stepMap.get(executionId);
          }
        }
      } else {
        // For regular mode, use pre-computed step name map
        if (nodeText) {
          matchingStep = stepNameMap.get(nodeText);
        }
        
        // Fallback: try to match by ID
        if (!matchingStep && nodeId && nodeId.startsWith('step_name_')) {
          const stepName = nodeId.replace('step_name_', '');
          matchingStep = stepNameMap.get(stepName);
        }
      }
      
      if (matchingStep) {
        // Make the node clickable
        nodeElement.style.cursor = 'pointer';
        nodeElement.style.transition = 'opacity 0.2s ease';
        
        const clickHandler = (e: Event) => {
          e.preventDefault();
          e.stopPropagation();
          onStepClick(matchingStep!);
        };
        
        const hoverInHandler = () => {
          nodeElement.style.opacity = '0.7';
          nodeElement.style.filter = 'drop-shadow(3px 3px 6px rgba(0,0,0,0.4)) brightness(1.1)';
        };
        
        const hoverOutHandler = () => {
          nodeElement.style.opacity = '1';
          nodeElement.style.filter = 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))';
        };
        
        nodeElement.addEventListener('click', clickHandler);
        nodeElement.addEventListener('mouseenter', hoverInHandler);
        nodeElement.addEventListener('mouseleave', hoverOutHandler);
        
        // Also make it visually obvious it's clickable
        nodeElement.style.filter = 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))';
      }
    }
    
    // Process next batch if there are more nodes
    if (endIndex < nodes.length) {
      setTimeout(() => processNodeBatch(endIndex), 0);
    }
  };
  
  // Start processing nodes in batches
  processNodeBatch(0);
};

// Create a function to compute differences between input and output
const computeDiff = (input: any, output: any): string => {
  if (!input || !output) return JSON.stringify(output, null, 2);
  
  try {
    // Convert to objects if they're strings
    const inputObj = typeof input === 'string' ? JSON.parse(input) : input;
    const outputObj = typeof output === 'string' ? JSON.parse(output) : output;
    
    // If output has error, just return the error
    if (outputObj.error) {
      return typeof outputObj.error === 'string'
        ? outputObj.error
        : outputObj.error.stack || outputObj.error.message || JSON.stringify(outputObj.error, null, 2);
    }
    
    const diff: Record<string, any> = {};
    
    // Compare output to input and only include differences
    Object.keys(outputObj).forEach(key => {
      // If key doesn't exist in input or value is different
      if (!(key in inputObj) || JSON.stringify(inputObj[key]) !== JSON.stringify(outputObj[key])) {
        diff[key] = outputObj[key];
      }
    });
    
    // If no differences found
    if (Object.keys(diff).length === 0) {
      return '// No differences found between input and output';
    }
    
    return JSON.stringify(diff, null, 2);
  } catch (e) {
    // If parsing fails, return the original output
    return typeof output === 'string' ? output : JSON.stringify(output, null, 2);
  }
};

export const WorkflowExecutionView: React.FC = () => {
  const { executionId, workflowName } = useParams<{ executionId: string; workflowId: string; workflowName: string; }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedStep, setSelectedStep] = useState<Step | null>(null);
  const mermaidRef = useRef<HTMLDivElement>(null);
  const fullscreenMermaidRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<number>(1000); // Default 1s
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [showFullOutput, setShowFullOutput] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAllExecutions, setShowAllExecutions] = useState(false);
  const [isFullscreenLoading, setIsFullscreenLoading] = useState(false);
  const [timelineViewMode, setTimelineViewMode] = useState<'tree' | 'time'>('tree');

  // Add modal state for rerun workflow
  const [isRerunModalOpen, setIsRerunModalOpen] = useState(false);
  const [jsonInput, setJsonInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState<'success' | 'error' | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState<'success' | 'error' | null>(null);

  const { data, loading: queryLoading, error, startPolling, stopPolling } = useGetWorkflowExecution({
    variables: { executionId },
    fetchPolicy: 'network-only',
  });
  
  const [startWorkflow] = useStartWorkflow();
  const [workflowStepRecover] = useWorkflowStepRecover();
  const [triggerScheduledStepEarly] = useTriggerScheduledStepEarly();

  const handleStepClick = useCallback((step: Step) => {
    setSelectedStep(step);
    setSearchParams((curParams) => {
      curParams.set("step", step.executionId);
      return curParams;
    });
  }, [setSearchParams]);

  // Check if a step is recoverable
  const isStepRecoverable = (step: Step): boolean => {
    // Basic conditions: no outgoing messages sent and not currently running
    const basicConditions = step.outgoingSentAt === null && step.executionStatus?.isRunning === false;
    
    // If step has a scheduledFor time, check if it's more than 1 minute in the past
    if (step.scheduledFor) {
      const scheduledTime = new Date(step.scheduledFor);
      const now = new Date();
      const timeDifferenceMs = now.getTime() - scheduledTime.getTime();
      const oneMinuteMs = 60 * 1000; // 1 minute in milliseconds
      
      return basicConditions && timeDifferenceMs > oneMinuteMs;
    }
    
    // For non-scheduled steps, if outgoingSentAt is null, endAt must be at least 1 minute ago
    if (step.outgoingSentAt === null && step.endAt) {
      const endTime = new Date(step.endAt);
      const now = new Date();
      const timeDifferenceMs = now.getTime() - endTime.getTime();
      const oneMinuteMs = 60 * 1000; // 1 minute in milliseconds
      
      return basicConditions && timeDifferenceMs > oneMinuteMs;
    }
    
    // For non-scheduled steps without endAt, use the original logic
    return basicConditions;
  };

  const handleTriggerScheduledStepEarly = async (step: Step) => {
    setIsTriggering(true);
    setTriggerResult(null);

    try {
      const { data: mutationData } = await triggerScheduledStepEarly({
        variables: {
          input: {
            executionId: step.executionId,
            workflowId: step.workflowId
          }
        }
      });

      if (mutationData?.triggerScheduledStepEarly?.success) {
        setTriggerResult('success');

        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setTriggerResult(null);
        }, 3000);

        if (data?.workflowExecution) {
          startPolling(pollingInterval || 1000);
        }
      } else {
        setTriggerResult('error');
      }
    } catch (error) {
      console.error('Error triggering scheduled step early:', error);
      setTriggerResult('error');
    } finally {
      setIsTriggering(false);
    }
  };

  // Check if any steps are recoverable
  const hasRecoverableSteps = (): boolean => {
    return filteredSteps.some(step => isStepRecoverable(step));
  };

  // Handle step recovery
  const handleStepRecover = async (step: Step) => {
    setIsRecovering(true);
    setRecoveryResult(null);
    
    try {
      const { data: mutationData } = await workflowStepRecover({
        variables: {
          input: {
            workflowId: workflowName!,
            executionId: step.executionId
          }
        }
      });
      
      if (mutationData?.workflowStepRecover?.success) {
        setRecoveryResult('success');
        // Auto-hide success message after 3 seconds
        setTimeout(() => {
          setRecoveryResult(null);
        }, 3000);
      } else {
        setRecoveryResult('error');
      }
    } catch (error) {
      console.error('Error recovering step:', error);
      setRecoveryResult('error');
    } finally {
      setIsRecovering(false);
    }
  };



  const handleRefreshMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRefreshMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRefreshIntervalChange = (interval: number) => {
    setPollingInterval(interval);
    if (interval === 0) {
      stopPolling();
    } else {
      startPolling(interval);
    }
    handleRefreshMenuClose();
  };

  const handleRerun = async () => {
    if (!workflowName || !jsonInput) return;
    
    try {
      setIsSubmitting(true);
      const { data: mutationData } = await startWorkflow({
        variables: {
          input: {
            workflowName: workflowName,
            payload: JSON.parse(jsonInput)
          }
        }
      });
      
      if (mutationData?.startWorkflow?.success) {
        setIsRerunModalOpen(false);
        setJsonInput("");
        navigate(`/developer/workflows/${workflowName}/executions/${mutationData.startWorkflow.workflowId}`);
      } else {
        console.error('Failed to start workflow');
        // TODO: Add error handling/notification
      }
    } catch (error) {
      console.error('Failed to start workflow:', error);
      // TODO: Add error handling/notification
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add function to open modal with prepopulated input
  const handleOpenRerunModal = () => {
    if (data?.workflowExecution?.steps[0]?.inputPayload) {
      setJsonInput(JSON.stringify(data.workflowExecution.steps[0].inputPayload, null, 2));
    }
    setIsRerunModalOpen(true);
  };

  const handleCloseRerunModal = () => {
    setIsRerunModalOpen(false);
    setJsonInput("");
  };

  const generateLinearIssueUrl = (step: Step): string => {
    // Extract error message from outputResults
    let errorMessage = 'Unknown error';
    if (step.outputResults?.error) {
      const error = step.outputResults.error;
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.stack) {
        errorMessage = error.stack;
      } else {
        errorMessage = JSON.stringify(error, null, 2);
      }
    } else if (typeof step.outputResults === 'object' && step.outputResults?.success === false) {
      errorMessage = JSON.stringify(step.outputResults, null, 2);
    }

    // Truncate error message for title (max 100 chars)
    const truncatedError = errorMessage.length > 100 
      ? errorMessage.substring(0, 100) + '...' 
      : errorMessage;

    // Format title - use workflowName or fallback
    const workflowNameSafe = workflowName || 'Unknown Workflow';
    const title = `[WORKFLOW] ${workflowNameSafe} ${step.stepName}: ${truncatedError}`;

    // Build description with context
    const inputPayloadStr = step.inputPayload 
      ? JSON.stringify(step.inputPayload, null, 2)
      : 'No input payload available';
    
    const outputErrorStr = step.outputResults?.error
      ? (typeof step.outputResults.error === 'string'
          ? step.outputResults.error
          : step.outputResults.error.stack || step.outputResults.error.message || JSON.stringify(step.outputResults.error, null, 2))
      : (typeof step.outputResults === 'object' && step.outputResults?.success === false
          ? JSON.stringify(step.outputResults, null, 2)
          : 'No error details available');

    // Construct the workflow execution URL
    const workflowUrl = `${window.location.origin}/developer/workflows/${encodeURIComponent(workflowNameSafe)}/executions/${executionId}`;

    const description = `## Workflow Error

**Workflow:** ${workflowNameSafe}
**Step:** ${step.stepName}

### Stack Trace
\`\`\`
${outputErrorStr}
\`\`\`

---

### Additional Details

**Execution Link:** ${workflowUrl}

**Execution ID:** ${step.executionId}  
**Step ID:** ${step.executionId}  
**Started At:** ${step.startedAt ? new Date(step.startedAt).toLocaleString() : 'N/A'}  
**Ended At:** ${step.endAt ? new Date(step.endAt).toLocaleString() : 'N/A'}  
**Attempt:** ${step.attempt || 1}

### Input Payload
\`\`\`json
${inputPayloadStr}
\`\`\`
`;

    // Encode URL parameters
    const encodedTitle = encodeURIComponent(title);
    const encodedDescription = encodeURIComponent(description);

    return `https://linear.app/team/BE/new?title=${encodedTitle}&description=${encodedDescription}`;
  };


  useEffect(() => {
    if (pollingInterval > 0) {
      startPolling(pollingInterval);
    }
    return () => stopPolling();
  }, [startPolling, stopPolling, pollingInterval]);

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: true,
      theme: 'default',
      securityLevel: 'loose',
      flowchart: {
        useMaxWidth: true,
        htmlLabels: true,
        curve: 'basis'
      }
    });
  }, []);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    const renderMermaid = async () => {
      if (!isMounted || !mermaidRef.current || !data?.workflowExecution?.steps) {
        return;
      }

      try {
        const steps = filterNonWorkflowSteps(data.workflowExecution.steps);
        
        // Completely disable Mermaid for very large workflows
        const MAX_STEPS = 1000;
        const limitedSteps = steps.length > MAX_STEPS ? steps.slice(0, MAX_STEPS) : steps;
        
        if (steps.length > MAX_STEPS) {
          console.warn(`Very large workflow detected: ${steps.length} steps. Disabling graph view for performance.`);
          const element = mermaidRef.current;
          if (element) {
            element.innerHTML = `
              <div style="padding: 40px; text-align: center; color: #666;">
                <Typography variant="h6" gutterBottom>Graph View Disabled</Typography>
                <Typography variant="body2">
                  This workflow has ${steps.length} steps, which is too large for the graph view.<br/>
                  Use the timeline view or step list below for detailed information.
                </Typography>
              </div>
            `;
          }
          return;
        }
        
        const mermaidGraph = generateMermaidDiagram(limitedSteps, showAllExecutions);
        console.log('Mermaid diagram (normal):\n', mermaidGraph);

        const element = mermaidRef.current;
        if (element) {
          element.innerHTML = '';
          try {
            const { svg } = await mermaid.render('mermaid-graph', mermaidGraph);
            element.innerHTML = svg;
            
            // Add click event listeners to the nodes
            addMermaidClickHandlers(element, limitedSteps, showAllExecutions, handleStepClick);
          } catch (renderError) {
            console.error('Error rendering Mermaid diagram:', renderError);
            element.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">Failed to render workflow diagram. The workflow may be too complex.</div>';
          }
        }
      } catch (error) {
        console.error('Error rendering mermaid:', error);
      }
    };

    renderMermaid();
  }, [data, isMounted, showAllExecutions, handleStepClick]);

  // Separate effect for fullscreen mermaid rendering
  useEffect(() => {
    const renderFullscreenMermaid = async () => {
      if (!isMounted || !data?.workflowExecution?.steps || !isFullscreen) {
        return;
      }

      setIsFullscreenLoading(true);

      // Small timeout to ensure the DOM element is ready
      await new Promise(resolve => setTimeout(resolve, 100));

      if (!fullscreenMermaidRef.current) {
        setIsFullscreenLoading(false);
        return;
      }

      try {
        const steps = filterNonWorkflowSteps(data.workflowExecution.steps);
        
        // Completely disable Mermaid for very large workflows
        const MAX_STEPS = 1000; // Very conservative limit
        const limitedSteps = steps.length > MAX_STEPS ? steps.slice(0, MAX_STEPS) : steps;
        
        if (steps.length > MAX_STEPS) {
          console.warn(`Very large workflow detected: ${steps.length} steps. Disabling fullscreen graph view for performance.`);
          const element = fullscreenMermaidRef.current;
          if (element) {
            element.innerHTML = `
              <div style="padding: 40px; text-align: center; color: #666;">
                <Typography variant="h6" gutterBottom>Graph View Disabled</Typography>
                <Typography variant="body2">
                  This workflow has ${steps.length} steps, which is too large for the graph view.<br/>
                  Use the timeline view or step list for detailed information.
                </Typography>
              </div>
            `;
          }
          setIsFullscreenLoading(false);
          return;
        }
        
        const mermaidGraph = generateMermaidDiagram(limitedSteps, showAllExecutions);
        console.log('Mermaid diagram (fullscreen):\n', mermaidGraph);

        const element = fullscreenMermaidRef.current;
        if (element) {
          element.innerHTML = '';
          try {
            const { svg } = await mermaid.render(`mermaid-graph-fullscreen-${Date.now()}`, mermaidGraph);
            element.innerHTML = svg;
            
            // Add click event listeners to the fullscreen nodes
            addMermaidClickHandlers(element, limitedSteps, showAllExecutions, handleStepClick);
          } catch (renderError) {
            console.error('Error rendering fullscreen Mermaid diagram:', renderError);
            element.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">Failed to render workflow diagram. The workflow may be too complex.</div>';
          }
        }
      } catch (error) {
        console.error('Error rendering fullscreen mermaid:', error);
      } finally {
        setIsFullscreenLoading(false);
      }
    };

    if (isFullscreen) {
      renderFullscreenMermaid();
    }
  }, [data, isMounted, showAllExecutions, isFullscreen, handleStepClick]);

  const getPollingLabel = (interval: number) => {
    if (interval === 0) return 'Disabled';
    if (interval === 1000) return '1s';
    if (interval === 5000) return '5s';
    if (interval === 60000) return '1m';
    return '';
  };

  // Reset output expansion when step changes
  useEffect(() => {
    setShowFullOutput(false);
    setRecoveryResult(null);
    setTriggerResult(null);
  }, [selectedStep]);

  const closeSelectedStep = () => {
    setSelectedStep(null);
    setSearchParams((curParams) => {
      curParams.delete("step");
      return curParams;
    });
  }

  useEffect(() => {
    if (!searchParams.get("step") || !data) {
      return;
    }

    const { steps } = data.workflowExecution;
    const filteredSteps = filterNonWorkflowSteps(steps);

    setSelectedStep(filteredSteps.filter(step => step.executionId === searchParams.get("step"))[0]);
  }, [searchParams, data]);

  if (queryLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error loading workflow execution: {error.message}</Typography>
      </Box>
    );
  }

  if (!data?.workflowExecution) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No workflow execution found</Typography>
      </Box>
    );
  }

  const { steps } = data.workflowExecution;
  const filteredSteps = filterNonWorkflowSteps(steps);
  const inputPayload = data.workflowExecution.steps[0]?.inputPayload;
  const hasQuickLinks = inputPayload?.userKey || inputPayload?.portfolioKey || inputPayload?.autoPilotSettingsKey;

  if (!steps.length || !filteredSteps.length) {
    return (
      <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate(`/developer/workflows/${workflowName}`)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Breadcrumbs>
            <Link component={RouterLink} to="/developer" color="inherit">
              Developer
            </Link>
            <Link component={RouterLink} to="/developer/workflows" color="inherit">
              Workflows
            </Link>
            <Link component={RouterLink} to={`/developer/workflows/${workflowName}`} color="inherit">
              {workflowName}
            </Link>
            <Typography color="text.primary">Execution {executionId}</Typography>
          </Breadcrumbs>
        </Box>

        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 2
        }}>
          <Typography variant="h4" color="text.secondary">
            NO DATA YET
          </Typography>
          <Typography variant="body1" color="text.secondary">
            The workflow execution has no steps to display.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Function to build proper tree order where children immediately follow their parent
  const buildTreeOrder = (steps: Step[]): Step[] => {
    // Create maps for efficient lookups
    const stepMap = new Map<string, Step>();
    const childrenMap = new Map<string, Step[]>();
    const rootSteps: Step[] = [];

    // Build the step map and children map
    steps.forEach(step => {
      stepMap.set(step.executionId, step);
      
      if (step.prevExecutionId) {
        if (!childrenMap.has(step.prevExecutionId)) {
          childrenMap.set(step.prevExecutionId, []);
        }
        childrenMap.get(step.prevExecutionId)!.push(step);
      } else {
        rootSteps.push(step);
      }
    });

    // Sort children by triggered time for consistent ordering
    childrenMap.forEach(children => {
      children.sort((a, b) => {
        const aTime = new Date(a.triggeredAt || '1970-01-01').getTime();
        const bTime = new Date(b.triggeredAt || '1970-01-01').getTime();
        return aTime - bTime;
      });
    });

    // Depth-first traversal to build tree order
    const result: Step[] = [];
    const visited = new Set<string>();
    const MAX_RECURSION_DEPTH = 100; // Prevent infinite recursion

    const traverse = (step: Step, depth: number = 0) => {
      // Prevent infinite recursion
      if (depth > MAX_RECURSION_DEPTH) {
        console.warn(`Maximum recursion depth reached for step ${step.executionId}. Possible circular reference detected.`);
        return;
      }
      
      if (visited.has(step.executionId)) return;
      
      visited.add(step.executionId);
      result.push(step);
      
      // Add all children immediately after the parent
      const children = childrenMap.get(step.executionId) || [];
      children.forEach(child => traverse(child, depth + 1));
    };

    // Start with root steps (steps with no parent)
    rootSteps.sort((a, b) => {
      const aTime = new Date(a.triggeredAt || '1970-01-01').getTime();
      const bTime = new Date(b.triggeredAt || '1970-01-01').getTime();
      return aTime - bTime;
    });

    rootSteps.forEach(rootStep => traverse(rootStep));

    return result;
  };

  const items: any[] = [];
  const groups: any[] = [];
  
  // Calculate timeline bounds including scheduled steps
  const allStepTimes = filteredSteps.map(step => {
    const times = [];
    if (step.startedAt) times.push(new Date(step.startedAt));
    if (step.scheduledFor) times.push(new Date(step.scheduledFor));
    if (step.endAt) times.push(new Date(step.endAt));
    return times;
  }).flat();
  
  let timelineStart = data.workflowExecution.startedAt ? new Date(data.workflowExecution.startedAt) : new Date();
  if (allStepTimes.length > 0) {
    const earliestTime = new Date(Math.min(...allStepTimes.map(t => t.getTime())));
    if (earliestTime < timelineStart) {
      timelineStart = earliestTime;
    }
  }
  
  let timelineEnd = data.workflowExecution.endAt ? new Date(data.workflowExecution.endAt) : new Date(timelineStart.getTime() + 5 * 60 * 1000);
  if (allStepTimes.length > 0) {
    const latestTime = new Date(Math.max(...allStepTimes.map(t => t.getTime())));
    if (latestTime > timelineEnd) {
      timelineEnd = latestTime;
    }
  }

  // Create a map of step dependencies for arrows
  const stepDependencies = new Map<string, string[]>();
  filteredSteps.forEach(step => {
    if (step.prevExecutionId) {
      const prevStep = filteredSteps.find(s => s.executionId === step.prevExecutionId);
      if (prevStep) {
        if (!stepDependencies.has(step.executionId)) {
          stepDependencies.set(step.executionId, []);
        }
        stepDependencies.get(step.executionId)!.push(prevStep.executionId);
      }
    }
  });



  // Limit steps for timeline to prevent performance issues
  const MAX_TIMELINE_STEPS = 500;
  const timelineSteps = filteredSteps.length > MAX_TIMELINE_STEPS 
    ? filteredSteps.slice(0, MAX_TIMELINE_STEPS) 
    : filteredSteps;
  
  // Disable tree view for very large workflows to prevent recursion issues
  const effectiveTimelineViewMode = filteredSteps.length > 200 ? 'time' : timelineViewMode;
  
  // Sort steps based on view mode
  const sortedSteps = effectiveTimelineViewMode === 'tree' 
    ? buildTreeOrder(timelineSteps) // Build proper tree order for tree view
    : [...timelineSteps].sort((a, b) => {
        const aTime = new Date(a.startedAt || a.triggeredAt || '1970-01-01').getTime();
        const bTime = new Date(b.startedAt || b.triggeredAt || '1970-01-01').getTime();
        return aTime - bTime;
      });

  sortedSteps.forEach((step, index) => {
    groups.push({
      id: step.executionId,
      content: step.stepName,
      order: timelineViewMode === 'tree' ? index : 
             new Date(step.startedAt || step.scheduledFor || step.triggeredAt || '1970-01-01').getTime(),
    });

    if (step.startedAt) {
      const hasError = step.outputResults?.error || (typeof step.outputResults === 'object' && step.outputResults?.success === false);
      const stepEnd = step.endAt ? new Date(step.endAt) : new Date();
      
      items.push({
          id: step.executionId,
          group: step.executionId,
          content: '',
          start: new Date(step.startedAt),
          end: stepEnd,
          style: `background-color: ${hasError ? '#d32f2f' : '#4CAF50'}; border: 1px solid ${hasError ? '#b71c1c' : '#388E3C'};`,
          className: hasError ? 'timeline-item-error' : 'timeline-item-success',
      });
    } else if (step.scheduledFor && new Date(step.scheduledFor) > new Date()) {
      // Show delayed steps as a point in time
      items.push({
          id: step.executionId,
          group: step.executionId,
          content: '',
          start: new Date(step.scheduledFor),
          end: new Date(step.scheduledFor),
          style: `background-color: #FF9800; border: 2px solid #F57C00; border-radius: 50%; width: 12px; height: 12px;`,
          className: 'timeline-item-delayed',
      });
    }
  });

  const timelineItems = new DataSet(items);
  const timelineGroups = new DataSet(groups);
  
  const timelineOptions = {
    stack: effectiveTimelineViewMode === 'time',
    maxHeight: '500px',
    zoomable: true,
    zoomMin: 100, // 100ms
    zoomMax: 1000 * 60 * 60 * 24, // 1 day
    start: timelineStart,
    end: timelineEnd,
    orientation: {
      axis: 'top',
      item: 'top'
    },
    groupOrder: effectiveTimelineViewMode === 'tree' ? 'order' : 'order',
    editable: false,
    showCurrentTime: true,
    groupTemplate: (group: any) => {
      if (group) {
        return group.content
      }
      return ''
    },
    groupOrderSwap: false,
    verticalScroll: true,
    horizontalScroll: true,
    multiselect: false,
    showTooltips: true,
    tooltip: {
      followMouse: true
    }
  };



  const generateMermaidDiagram = (steps: Step[], showAllExecs = false) => {
    const nodes = new Set<string>();
    const edges = new Set<string>();
    const unstartedSteps = new Set<string>();
    
    if (showAllExecs) {
      // Show each execution as a separate node
      const stepCounts = new Map<string, number>();
      const stepNodeIds = new Map<string, string>();
      const nodeLabels = new Map<string, string>();
      
      // Pre-compute step relationships for efficiency
      const stepByExecutionId = new Map<string, Step>();
      const childrenByParentId = new Map<string, Step[]>();
      
      steps.forEach(step => {
        stepByExecutionId.set(step.executionId, step);
        
        if (step.prevExecutionId) {
          if (!childrenByParentId.has(step.prevExecutionId)) {
            childrenByParentId.set(step.prevExecutionId, []);
          }
          childrenByParentId.get(step.prevExecutionId)!.push(step);
        }
      });
      
      // Create unique node IDs for each execution
      steps.forEach(step => {
        const count = (stepCounts.get(step.stepName) || 0) + 1;
        stepCounts.set(step.stepName, count);
        
        // Use execution ID as node ID for precise matching
        const nodeId = `step_${step.executionId}`;
        const displayLabel = `${step.stepName}${step.attempt && step.attempt > 1 ? ` (Retry ${step.attempt})` : ''}`;
        
        stepNodeIds.set(step.executionId, nodeId);
        nodeLabels.set(nodeId, displayLabel);
        nodes.add(nodeId);
      });
      
      // Create edges based on execution relationships
      steps.forEach(step => {
        if (step.prevExecutionId) {
          const prevNodeId = stepNodeIds.get(step.prevExecutionId);
          const currentNodeId = stepNodeIds.get(step.executionId);
          if (prevNodeId && currentNodeId) {
            edges.add(`${prevNodeId} --> ${currentNodeId}`);
          }
        }
      });

      // Add outgoing jobs for ALL steps that have them
      steps.forEach(step => {
        const outgoingJobs = (step.outgoingJobs && step.outgoingJobs !== '#') ? step.outgoingJobs : {};
        Object.entries(outgoingJobs).forEach(([jobId, jobName]) => {
          // Check if this outgoing job actually executed as a direct child using pre-computed relationships
          const children = childrenByParentId.get(step.executionId) || [];
          const hasExecutedChild = children.some(childStep => childStep.stepName === jobName);
          
          // If the job didn't execute as a direct child, add it as unstarted
          if (!hasExecutedChild) {
            // Use a unique node ID for unstarted jobs to avoid conflicts
            const unstartedNodeId = `unstarted_${jobName}_from_${step.executionId}`;
            nodes.add(unstartedNodeId);
            nodeLabels.set(unstartedNodeId, jobName);
            unstartedSteps.add(unstartedNodeId);
            const currentNodeId = stepNodeIds.get(step.executionId);
            if (currentNodeId) {
              edges.add(`${currentNodeId} --> ${unstartedNodeId}`);
            }
          }
        });
      });

      const nodeStyles = Array.from(nodes).map(nodeId => {
        const isUnstarted = unstartedSteps.has(nodeId);
        const displayLabel = nodeLabels.get(nodeId) || nodeId;
        
        // Find the step for this node using pre-computed map
        let step: Step | undefined;
        if (!isUnstarted) {
          // Extract execution ID from node ID
          const executionId = nodeId.replace('step_', '');
          step = stepByExecutionId.get(executionId);
        }
        
        const hasError = step?.outputResults?.error || (typeof step?.outputResults === 'object' && step?.outputResults?.success === false);
        const isDelayed = step?.scheduledFor && new Date(step.scheduledFor) > new Date();
        
        let style = '';
        if (isUnstarted) {
          style = ':::unstarted';
        } else if (step?.endAt) {
          style = hasError ? ':::error' : ':::completed';
        } else if (step?.startedAt) {
          style = ':::running';
        } else if (isDelayed) {
          style = ':::delayed';
        } else {
          style = ':::pending';
        }
        
        // Use mermaid label syntax: nodeId["Display Label"] or special shape for delayed
        if (isDelayed) {
          return `    ${nodeId}("${displayLabel}")${style}`;
        }
        return `    ${nodeId}["${displayLabel}"]${style}`;
      }).join('\n');

      const edgeStyles = Array.from(edges).map(edge => {
        return `    ${edge}`;
      }).join('\n');

      return `
        graph TD
          ${nodeStyles}
          ${edgeStyles}
          
          classDef unstarted fill:#f5f5f5,stroke:#999,stroke-dasharray: 5 5
          classDef error fill:#d32f2f,stroke:#b71c1c
          classDef completed fill:#4CAF50,stroke:#388E3C
          classDef running fill:#FFC107,stroke:#FFA000
          classDef pending fill:#9E9E9E,stroke:#616161
          classDef delayed fill:#FF9800,stroke:#F57C00,stroke-dasharray: 3 3
      `;
    } else {
      // Original logic - show latest step for each step name
      // Create a map to get the latest step for each step name
      const latestStepByName = new Map<string, Step>();
      steps.forEach(step => {
        const existing = latestStepByName.get(step.stepName);
        if (!existing || (step.triggeredAt && (!existing.triggeredAt || step.triggeredAt > existing.triggeredAt))) {
          latestStepByName.set(step.stepName, step);
        }
      });
      
      // Pre-compute step relationships for efficiency
      const stepByExecutionId = new Map<string, Step>();
      const childrenByParentId = new Map<string, Step[]>();
      
      steps.forEach(step => {
        stepByExecutionId.set(step.executionId, step);
        
        if (step.prevExecutionId) {
          if (!childrenByParentId.has(step.prevExecutionId)) {
            childrenByParentId.set(step.prevExecutionId, []);
          }
          childrenByParentId.get(step.prevExecutionId)!.push(step);
        }
      });
      
      // First, build the normal workflow graph
      steps.forEach(step => {
        // Use step name as node ID for regular mode
        nodes.add(`step_name_${step.stepName}`);
        
        if (step.prevExecutionId) {
          const prevStep = steps.find(s => s.executionId === step.prevExecutionId);
          if (prevStep) {
            edges.add(`step_name_${prevStep.stepName} --> step_name_${step.stepName}`);
          }
        }
      });

      // Add outgoing jobs for ALL steps that have them
      steps.forEach(step => {
        const outgoingJobs = (step.outgoingJobs && step.outgoingJobs !== '#') ? step.outgoingJobs : {};
        Object.entries(outgoingJobs).forEach(([jobId, jobName]) => {
          // Check if this outgoing job actually executed using pre-computed relationships
          const children = childrenByParentId.get(step.executionId) || [];
          const hasExecutedChild = children.some(childStep => childStep.stepName === jobName);
          
          // If the job didn't execute as a direct child, add it as unstarted
          if (!hasExecutedChild) {
            nodes.add(`step_name_${jobName}`);
            unstartedSteps.add(`step_name_${jobName}`);
            edges.add(`step_name_${step.stepName} --> step_name_${jobName}`);
          }
        });
      });

      const nodeStyles = Array.from(nodes).map(node => {
        const isUnstarted = unstartedSteps.has(node);
        const stepName = node.replace('step_name_', '');
        const step = latestStepByName.get(stepName); // Use latest step instead of first found
        const hasError = step?.outputResults?.error || (typeof step?.outputResults === 'object' && step?.outputResults?.success === false);
        const isDelayed = step?.scheduledFor && new Date(step.scheduledFor) > new Date();
        
        let style = '';
        if (isUnstarted) {
          style = ':::unstarted';
        } else if (step?.endAt) {
          style = hasError ? ':::error' : ':::completed';
        } else if (step?.startedAt) {
          style = ':::running';
        } else if (isDelayed) {
          style = ':::delayed';
        } else {
          style = ':::pending';
        }
        
        // Use mermaid label syntax: nodeId["Display Label"] or special shape for delayed
        // Collapsed mode: show only step name (no retry badge)
        const label = `${stepName}`;
        if (isDelayed) {
          return `    ${node}("${label}")${style}`;
        }
        return `    ${node}["${label}"]${style}`;
      }).join('\n');

      const edgeStyles = Array.from(edges).map(edge => {
        return `    ${edge}`;
      }).join('\n');

      return `
        graph TD
          ${nodeStyles}
          ${edgeStyles}
          
          classDef unstarted fill:#f5f5f5,stroke:#999,stroke-dasharray: 5 5
          classDef error fill:#d32f2f,stroke:#b71c1c
          classDef completed fill:#4CAF50,stroke:#388E3C
          classDef running fill:#FFC107,stroke:#FFA000
          classDef pending fill:#9E9E9E,stroke:#616161
          classDef delayed fill:#FF9800,stroke:#F57C00,stroke-dasharray: 3 3
      `;
    }
  };

  return (
    <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate(`/developer/workflows/${workflowName}`)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Breadcrumbs>
          <Link component={RouterLink} to="/developer" color="inherit">
            Developer
          </Link>
          <Link component={RouterLink} to="/developer/workflows" color="inherit">
            Workflows
          </Link>
          <Link component={RouterLink} to={`/developer/workflows/${workflowName}`} color="inherit">
            {workflowName}
          </Link>
          <Typography color="text.primary">Execution {executionId}</Typography>
        </Breadcrumbs>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh rate">
          <Button
            onClick={handleRefreshMenuClick}
            size="small"
            variant="outlined"
            startIcon={pollingInterval === 0 ? <UpdateDisabledIcon /> : <UpdateIcon />}
            sx={{ mr: 2 }}
          >
            {getPollingLabel(pollingInterval)}
          </Button>
        </Tooltip>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleOpenRerunModal}
          disabled={queryLoading}
          startIcon={<RefreshIcon />}
          sx={{ ml: 2 }}
        >
          Rerun Workflow
        </Button>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleRefreshMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          onClick={() => handleRefreshIntervalChange(0)}
          selected={pollingInterval === 0}
        >
          <ListItemIcon>
            <UpdateDisabledIcon fontSize="small" />
          </ListItemIcon>
          Disabled
        </MenuItem>
        <MenuItem 
          onClick={() => handleRefreshIntervalChange(1000)}
          selected={pollingInterval === 1000}
        >
          <ListItemIcon>
            <UpdateIcon fontSize="small" />
          </ListItemIcon>
          1 second
        </MenuItem>
        <MenuItem 
          onClick={() => handleRefreshIntervalChange(5000)}
          selected={pollingInterval === 5000}
        >
          <ListItemIcon>
            <UpdateIcon fontSize="small" />
          </ListItemIcon>
          5 seconds
        </MenuItem>
        <MenuItem 
          onClick={() => handleRefreshIntervalChange(60000)}
          selected={pollingInterval === 60000}
        >
          <ListItemIcon>
            <UpdateIcon fontSize="small" />
          </ListItemIcon>
          1 minute
        </MenuItem>
      </Menu>

      <Typography variant="h4" gutterBottom>
        Workflow Execution
      </Typography>

      {/* Global Recovery Alert */}
      {hasRecoverableSteps() && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3 }}
          action={
            <Button 
              color="warning" 
              variant="outlined" 
              size="small"
              startIcon={<RecoveryIcon />}
              onClick={() => {
                // Find the first recoverable step and open its details
                const firstRecoverableStep = filteredSteps.find(step => isStepRecoverable(step));
                if (firstRecoverableStep) {
                  handleStepClick(firstRecoverableStep);
                }
              }}
            >
              View Recoverable Steps
            </Button>
          }
        >
          This workflow has steps that can be recovered. Click "View Recoverable Steps" to see details.
        </Alert>
      )}

      {/* Large Workflow Warning */}
      {filteredSteps.length > 50 && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
        >
          Large workflow detected: {filteredSteps.length} steps. The graph view is disabled for workflows with more than 50 steps for performance. 
          Use the timeline view or step list for full details.
        </Alert>
      )}

      <Paper 
        sx={{ 
          p: 3, 
          background: 'linear-gradient(145deg, #f5f5f5, #ffffff)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          borderRadius: '8px'
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 1
            }}>
              <Typography variant="subtitle2" color="text.secondary">
                Execution Timeline
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                p: 1.5,
                borderRadius: '4px',
                bgcolor: 'background.default'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Started
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {new Date(data.workflowExecution.startedAt).toLocaleString()}
                </Typography>
              </Box>
              {data.workflowExecution.endAt && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  p: 1.5,
                  borderRadius: '4px',
                  bgcolor: 'background.default'
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Completed
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {new Date(data.workflowExecution.endAt).toLocaleString()}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'success.main',
                        fontWeight: 500,
                        mt: 0.5
                      }}
                    >
                      {formatTimeDifference(new Date(data.workflowExecution.startedAt), new Date(data.workflowExecution.endAt))} total runtime
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 1
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Input Payload
                </Typography>
                {hasQuickLinks && (
                  <Box sx={{ display: 'flex', gap: 1, mr: 2, alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                      Quick Links
                    </Typography>
                    {inputPayload.userKey && (
                      <Tooltip title="View User">
                        <IconButton
                          component={RouterLink}
                          to={`/users/${inputPayload.userKey}`}
                          size="small"
                          sx={{ p: 0.5, borderRadius: '8px' }}
                        >
                          <PersonIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {inputPayload.portfolioKey && (
                      <Tooltip title="View Portfolio">
                        <IconButton
                          component={RouterLink}
                          to={`/portfolios/${inputPayload.portfolioKey}`}
                          size="small"
                          sx={{ p: 0.5, borderRadius: '8px' }}
                        >
                          <AccountBalanceWalletIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {inputPayload.autoPilotSettingsKey && (
                      <Tooltip title="View Tracker Details">
                        <IconButton
                          component={RouterLink}
                          to={`/pilot-details/${inputPayload.autoPilotSettingsKey}`}
                          size="small"
                          sx={{ p: 0.5, borderRadius: '8px' }}
                        >
                          <ShowChartIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
              <Paper 
                sx={{ 
                  p: 2,
                  borderRadius: '4px',
                  bgcolor: 'background.default',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}
              >
                <JsonViewer 
                  value={data.workflowExecution.steps[0]?.inputPayload || {}}
                  height="200px"
                  maxLines={15}
                  showExpandButton={true}
                />
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Workflow Graph
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showAllExecutions}
                        onChange={(e) => setShowAllExecutions(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        Show All
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  <Tooltip title={isFullscreen ? "Exit fullscreen" : "View fullscreen"}>
                    <IconButton 
                      onClick={() => setIsFullscreen(true)}
                      size="small"
                    >
                      <FullscreenIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Box 
                sx={{ 
                  flex: 1, 
                  overflow: 'auto', 
                  minHeight: 0, 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  border: '1px dashed #ccc',
                  p: 2
                }}
              >
                {isMounted && (
                  <div 
                    ref={mermaidRef} 
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      minHeight: '300px',
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }} 
                  />
                )}
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Execution Timeline
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant={timelineViewMode === 'tree' ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<AccountTreeIcon />}
                    onClick={() => setTimelineViewMode('tree')}
                    disabled={filteredSteps.length > 200}
                    sx={{ minWidth: 'auto' }}
                  >
                    Tree
                  </Button>
                  <Button
                    variant={timelineViewMode === 'time' ? 'contained' : 'outlined'}
                    size="small"
                    startIcon={<ViewTimelineIcon />}
                    onClick={() => setTimelineViewMode('time')}
                    sx={{ minWidth: 'auto' }}
                  >
                    Time
                  </Button>
                </Box>
              </Box>
              {filteredSteps.length > 200 && effectiveTimelineViewMode !== 'tree' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Tree view is disabled for workflows with more than 200 steps to prevent performance issues.
                </Alert>
              )}
              <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, '& .vis-timeline': {
                border: '1px solid #ccc',
                borderRadius: '4px'
              } }}>
                <TimelineChart
                  items={timelineItems}
                  groups={timelineGroups}
                  options={timelineOptions}
                />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Steps
              </Typography>
              <List>
                {filteredSteps.map((step: Step) => (
                  <React.Fragment key={step.executionId}>
                    <ListItem
                      button
                      onClick={() => handleStepClick(step)}
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderLeft: '4px solid',
                        borderLeftColor: getStatusColor(step) === 'error' ? 'error.main' : 'transparent'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {step.stepName}
                              {step.executionStatus?.isRunning && (
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: 'success.main',
                                    animation: 'pulse 2s infinite',
                                    '@keyframes pulse': {
                                      '0%': {
                                        opacity: 1,
                                      },
                                      '50%': {
                                        opacity: 0.5,
                                      },
                                      '100%': {
                                        opacity: 1,
                                      },
                                    },
                                  }}
                                />
                              )}
                          {step.startedAt && !step.endAt && !step.executionStatus?.isRunning && (
                                <Tooltip title="Heartbeat failed to report for this job">
                                  <WarningIcon 
                                    sx={{ 
                                      color: 'warning.main', 
                                      fontSize: '1rem',
                                      cursor: 'help'
                                    }} 
                                  />
                                </Tooltip>
                              )}
                          {step?.attempt && step.attempt > 1 && (
                            <Chip 
                              size="small" 
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <UpdateIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />
                                  <>{step.attempt}</>
                                </Box>
                              }
                              color="warning"
                              sx={{ ml: 1 }}
                            />
                          )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                Started: {step.startedAt ? new Date(step.startedAt).toLocaleString() : 'Not started'}
                              </Typography>
                              {step.scheduledFor && new Date(step.scheduledFor) > new Date() && !step.startedAt && (
                                <CountdownTimer scheduledFor={step.scheduledFor} />
                              )}
                            </Box>
                          }
                        />
                      </Box>
                      <Chip 
                        label={
                          step.endAt ? (getStatusColor(step) === 'error' ? 'Failed' : 'Completed') : 
                          step.startedAt ? 'Running' : 
                          step.scheduledFor && new Date(step.scheduledFor) > new Date() ? 'Delayed' : 'Pending'
                        } 
                        color={getStatusColor(step)}
                        size="small"
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Drawer
        anchor="right"
        open={!!selectedStep}
        onClose={closeSelectedStep}
        PaperProps={{ 
          sx: { 
            width: '80%',
            maxWidth: '1500px',
            boxShadow: 3,
            mt: '64px',
            height: 'calc(100% - 64px)'
          } 
        }}
      >
        {selectedStep && (
          <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: 2,
              mb: 3,
              pb: 3,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 500 }}>
                    {selectedStep.stepName}
                  </Typography>
                  <Chip 
                    label={
                      selectedStep.endAt ? 
                        (getStatusColor(selectedStep) === 'error' ? 'Failed' : 'Completed') : 
                        selectedStep.startedAt ? 'Running' : 
                        selectedStep.scheduledFor && new Date(selectedStep.scheduledFor) > new Date() ? 'Delayed' : 'Pending'
                    } 
                    color={getStatusColor(selectedStep)}
                    size="small"
                  />
                  {selectedStep?.attempt && selectedStep.attempt > 1 && (
                    <Chip 
                      size="small" 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <UpdateIcon sx={{ fontSize: '1rem', verticalAlign: 'middle' }} />
                          <>{selectedStep.attempt}</>
                        </Box>
                      }
                      color="warning"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {(getStatusColor(selectedStep) === 'error' || selectedStep.outputResults?.error || (typeof selectedStep.outputResults === 'object' && selectedStep.outputResults?.success === false)) && (
                    <Button
                      component="a"
                      href={generateLinearIssueUrl(selectedStep)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<LinearAppIcon />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'none'
                        }
                      }}
                    >
                      Create Issue
                    </Button>
                  )}
                  <IconButton onClick={closeSelectedStep} size="small">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
                {selectedStep?.attempt && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: '200px'
                  }}>
                    {selectedStep.attempt > 1 && selectedStep.originalStepId && (
                      <Typography variant="caption" color="text.secondary">
                        Original Step ID: {selectedStep.originalStepId}
                      </Typography>
                    )}
                  </Box>
                )}
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                flexWrap: 'wrap'
              }}>
                {selectedStep.scheduledFor && new Date(selectedStep.scheduledFor) > new Date() && !selectedStep.startedAt && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: '200px'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Scheduled For
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {new Date(selectedStep.scheduledFor).toLocaleString()}
                      </Typography>
                      <CountdownTimer scheduledFor={selectedStep.scheduledFor} />
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleTriggerScheduledStepEarly(selectedStep)}
                        disabled={isTriggering}
                        sx={{ mt: 1 }}
                      >
                        {isTriggering ? 'Running...' : 'Run Now'}
                      </Button>
                    </Box>
                  </Box>
                )}
                {selectedStep.triggeredAt && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: '200px'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Triggered
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {new Date(selectedStep.triggeredAt).toLocaleString()}
                    </Typography>
                  </Box>
                )}
                
                {selectedStep.scheduledFor && !selectedStep.startedAt && new Date(selectedStep.scheduledFor) <= new Date() && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: '200px'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Scheduled For
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {new Date(selectedStep.scheduledFor).toLocaleString()}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'warning.main',
                        fontWeight: 500
                      }}
                    >
                      Overdue
                    </Typography>
                  </Box>
                )}
                
                {selectedStep.startedAt && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: '200px'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Started
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {new Date(selectedStep.startedAt).toLocaleString()}
                      </Typography>
                      {selectedStep.triggeredAt && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'primary.main',
                            fontWeight: 500
                          }}
                        >
                          {formatTimeDifference(new Date(selectedStep.triggeredAt), new Date(selectedStep.startedAt))} to start
                          {selectedStep.scheduledFor && (
                            <Typography 
                              component="span" 
                              variant="caption" 
                              sx={{ 
                                color: 'warning.main',
                                fontWeight: 500,
                                ml: 1
                              }}
                            >
                              (scheduled)
                            </Typography>
                          )}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                
                {selectedStep.endAt && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: '200px'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Completed
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {new Date(selectedStep.endAt).toLocaleString()}
                      </Typography>
                      {selectedStep.startedAt && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'success.main',
                            fontWeight: 500
                          }}
                        >
                          {formatTimeDifference(new Date(selectedStep.startedAt), new Date(selectedStep.endAt))} runtime
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {selectedStep.outgoingSentAt && (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: '200px'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Post-flight
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {new Date(selectedStep.outgoingSentAt).toLocaleString()}
                      </Typography>
                      {selectedStep.endAt && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'primary.main',
                            fontWeight: 500
                          }}
                        >
                          {formatTimeDifference(new Date(selectedStep.endAt), new Date(selectedStep.outgoingSentAt))} runtime
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                {selectedStep.outgoingJobs && selectedStep.outgoingJobs !== '#' && typeof selectedStep.outgoingJobs === 'object' && Object.keys(selectedStep.outgoingJobs).length > 0 && (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: '200px'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Outgoing Jobs
                    </Typography>
                    <Tooltip title={
                      <React.Fragment>
                        <Typography color="inherit" sx={{ fontWeight: 'bold' }}>Triggered Steps</Typography>
                        <List dense sx={{ pl: 2 }}>
                          {Object.values(selectedStep.outgoingJobs).map(name => (
                            <ListItem key={name} sx={{ display: 'list-item', p: 0 }}>
                              {name}
                            </ListItem>
                          ))}
                        </List>
                      </React.Fragment>
                    }>
                      <Typography variant="body2" sx={{ fontWeight: 500, cursor: 'help' }}>
                        {Object.keys(selectedStep.outgoingJobs).length} steps triggered
                      </Typography>
                    </Tooltip>
                  </Box>
                )}

                {/* Heartbeat information - only show for running steps */}
                {selectedStep.startedAt && !selectedStep.endAt && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 0.5,
                    minWidth: '200px'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Heartbeat Status
                      </Typography>
                      {!selectedStep.executionStatus?.isRunning && (
                        <Tooltip title="Heartbeat failed to report for this job">
                          <WarningIcon 
                            sx={{ 
                              color: 'warning.main', 
                              fontSize: '0.875rem'
                            }} 
                          />
                        </Tooltip>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {selectedStep.executionStatus?.isRunning ? 'Active' : 'Inactive'}
                      </Typography>
                      {selectedStep.executionStatus?.lastHeartbeatAt && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: selectedStep.executionStatus.isRunning ? 'success.main' : 'warning.main',
                            fontWeight: 500
                          }}
                        >
                          Last: {new Date(selectedStep.executionStatus.lastHeartbeatAt).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Trigger Scheduled Step Early Alert */}
            {selectedStep && selectedStep.scheduledFor && new Date(selectedStep.scheduledFor) > new Date() && !selectedStep.startedAt && (
              <>
                {isTriggering ? (
                  <Alert
                    severity="info"
                    sx={{ mb: 3 }}
                    action={
                      <CircularProgress size={20} />
                    }
                  >
                    Triggering step...
                  </Alert>
                ) : triggerResult === 'success' ? (
                  <Alert
                    severity="success"
                    sx={{ mb: 3 }}
                    action={
                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setTriggerResult(null)}
                      >
                        <CloseIcon />
                      </IconButton>
                    }
                  >
                    Step triggered successfully!
                  </Alert>
                ) : triggerResult === 'error' ? (
                  <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    action={
                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setTriggerResult(null)}
                      >
                        <CloseIcon />
                      </IconButton>
                    }
                  >
                    Failed to trigger step. Please try again.
                  </Alert>
                ) : null}
              </>
            )}

            {/* Recovery Alert - show if step is recoverable */}
            {selectedStep && isStepRecoverable(selectedStep) && (
              <>
                {isRecovering ? (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 3 }}
                    action={
                      <CircularProgress size={20} />
                    }
                  >
                    Recovering step...
                  </Alert>
                ) : recoveryResult === 'success' ? (
                  <Alert 
                    severity="success" 
                    sx={{ mb: 3 }}
                    action={
                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setRecoveryResult(null)}
                      >
                        <CloseIcon />
                      </IconButton>
                    }
                  >
                    Step recovery initiated successfully!
                  </Alert>
                ) : recoveryResult === 'error' ? (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 3 }}
                    action={
                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setRecoveryResult(null)}
                      >
                        <CloseIcon />
                      </IconButton>
                    }
                  >
                    Failed to recover step. Please try again.
                  </Alert>
                ) : (
                  <Alert 
                    severity="warning" 
                    sx={{ mb: 3 }}
                    action={
                      <Button 
                        color="warning" 
                        variant="contained" 
                        size="small"
                        startIcon={<RecoveryIcon />}
                        onClick={() => handleStepRecover(selectedStep)}
                        disabled={isRecovering}
                      >
                        Recover Now
                      </Button>
                    }
                  >
                    This step is recoverable!
                  </Alert>
                )}
              </>
            )}
            
            <Box sx={{ overflow: 'auto', flex: 1 }}>
              {selectedStep?.inputPayload && (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    Input Payload
                  </Typography>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <JsonViewer 
                      value={selectedStep.inputPayload}
                      height="300px"
                      maxLines={20}
                      showExpandButton={true}
                    />
                  </Paper>
                </>
              )}

              {selectedStep?.outputResults && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>Output Results</Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!showFullOutput}
                          onChange={() => setShowFullOutput(!showFullOutput)}
                          color="warning"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: !showFullOutput ? 'warning.main' : 'text.secondary' }}>
                          Show Diff Only
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  </Typography>
                  <Paper 
                    sx={{ 
                      p: 2, 
                      mb: 2,
                      borderLeft: '4px solid',
                      borderLeftColor: getStatusColor(selectedStep) === 'error' ? 'error.main' : 'success.main'
                    }}
                  >
                    {(() => {
                      // Get output content
                      const fullOutputContent = selectedStep.outputResults.error ? 
                        selectedStep.outputResults.error.stack || selectedStep.outputResults.error.message || 
                        JSON.stringify(selectedStep.outputResults.error, null, 2) :
                        JSON.stringify(selectedStep.outputResults, null, 2);
                      
                      // Calculate diff if not showing full output
                      const diffContent = showFullOutput ? 
                        fullOutputContent : 
                        computeDiff(selectedStep.inputPayload, selectedStep.outputResults);
                      
                      const content = showFullOutput ? fullOutputContent : diffContent;
                      
                      return (
                        <JsonViewer 
                          value={content}
                          height="300px"
                          maxLines={40}
                          showExpandButton={true}
                        />
                      );
                    })()}
                  </Paper>
                </>
              )}

              <Typography variant="subtitle1" gutterBottom>
                Logs
              </Typography>
              <Paper sx={{ 
                p: 2, 
                height: 'calc(100vh - 400px)', /* Account for app bar (~64px) + header section (~150px) + spacing/margins (~30px) */
                display: 'flex',
                flexDirection: 'column',
                mb: 3
              }}>
                <LogViewer 
                  workflowId={data.workflowExecution.steps[0]?.workflowId || ''}
                  stepId={selectedStep.executionId}
                />
              </Paper>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Fullscreen Mermaid Dialog */}
      <Dialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            height: '90vh',
            maxWidth: 'none',
            maxHeight: 'none'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Workflow Graph - Fullscreen</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showAllExecutions}
                  onChange={(e) => setShowAllExecutions(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Show All Executions
                </Typography>
              }
              sx={{ m: 0 }}
            />
            <IconButton onClick={() => setIsFullscreen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          <Box 
            sx={{ 
              flex: 1, 
              overflow: 'auto', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              p: 2,
              position: 'relative'
            }}
          >
            {isFullscreenLoading && (
              <Box sx={{ 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)',
                zIndex: 1
              }}>
                <CircularProgress size={40} />
              </Box>
            )}
            {isMounted && isFullscreen && (
              <div 
                ref={fullscreenMermaidRef}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  minHeight: '600px',
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  opacity: isFullscreenLoading ? 0.3 : 1
                }} 
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFullscreen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rerun Workflow Dialog */}
      <Dialog
        open={isRerunModalOpen}
        onClose={handleCloseRerunModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Rerun Workflow</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Edit the input payload below and click "Rerun" to start a new workflow execution.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <CustomJsonEditor
              value={jsonInput}
              onChange={setJsonInput}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRerunModal} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleRerun}
            variant="contained"
            color="primary"
            disabled={isSubmitting || !jsonInput}
          >
            {isSubmitting ? 'Rerunning...' : 'Rerun'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 